import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Clock, Users, Vote, CheckCircle, Star, ArrowRight } from "lucide-react";
import NavigationBar from "@/components/NavigationBar";
import { useNavigate } from "react-router-dom";

const EventPlanningGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-background">
      <NavigationBar />
      
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold bg-gradient-hero bg-clip-text text-transparent mb-6">
              The Complete <strong>Event Planning Guide</strong>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Master the art of <strong>collaborative event scheduling</strong> with our comprehensive guide. Learn <strong>how to schedule an event with multiple people</strong> using modern <strong>scheduling tools</strong> and <strong>availability polls</strong>.
            </p>
            <Button 
              variant="hero" 
              size="xl" 
              onClick={() => navigate('/auth')}
              className="text-lg px-12 py-6 rounded-2xl shadow-elevated hover:shadow-glow transition-all"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Start Planning Your Event
            </Button>
          </div>

          {/* Table of Contents */}
          <Card className="mb-16 border-0 shadow-glass bg-gradient-card backdrop-blur-glass">
            <CardHeader>
              <h2 className="text-3xl font-bold text-center">Table of Contents</h2>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  "How to Find a Date That Works for Everyone",
                  "Meeting Time Finder Best Practices", 
                  "Creating Effective Availability Polls",
                  "Alternatives to Doodle for Group Scheduling",
                  "Free Scheduling Tool Comparison",
                  "Simple Date Picker for Events Guide"
                ].map((topic, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg hover:bg-primary/5 transition-colors">
                    <CheckCircle className="w-5 h-5 text-success" />
                    <span className="font-medium">{topic}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Section 1: Family Reunion Planning */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold mb-8 text-center">
              How to Plan <strong>Family Reunion Dates</strong> That Work for Everyone
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-0 shadow-glass bg-gradient-card backdrop-blur-glass">
                <CardHeader>
                  <h3 className="text-2xl font-semibold">Step 1: Create Your Event</h3>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Start by setting up your <strong>family reunion planning</strong> event with multiple date options. 
                    Consider holidays, school schedules, and travel requirements when selecting potential dates.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-warning" />
                      <span>Choose 3-5 potential weekend dates</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-warning" />
                      <span>Include holiday weekends for maximum attendance</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-warning" />
                      <span>Consider travel time for out-of-state relatives</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-glass bg-gradient-card backdrop-blur-glass">
                <CardHeader>
                  <h3 className="text-2xl font-semibold">Step 2: Invite All Family Members</h3>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Use <strong>collaborative event scheduling tools</strong> to reach everyone efficiently. 
                    Send invitations via email, text, or shareable links to ensure maximum participation.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-warning" />
                      <span>Email invitations for tech-savvy relatives</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-warning" />
                      <span>Text message links for quick responses</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-warning" />
                      <span>Phone calls for elderly family members</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Section 2: Business Meeting Scheduling */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold mb-8 text-center">
              <strong>Business Meeting Scheduling</strong> Best Practices
            </h2>
            <Card className="border-0 shadow-glass bg-gradient-card backdrop-blur-glass">
              <CardContent className="p-8">
                <p className="text-lg text-muted-foreground mb-6">
                  Professional <strong>business meeting scheduling tools</strong> have revolutionized how organizations coordinate. 
                  Here's how to schedule board meetings, team meetings, and corporate events efficiently.
                </p>
                
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Clock className="w-8 h-8 text-blue-500" />
                    </div>
                    <h3 className="font-semibold mb-2">Time Zone Coordination</h3>
                    <p className="text-sm text-muted-foreground">
                      Handle global teams with automatic time zone conversion and optimal meeting time suggestions.
                    </p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-green-500" />
                    </div>
                    <h3 className="font-semibold mb-2">Stakeholder Participation</h3>
                    <p className="text-sm text-muted-foreground">
                      Ensure all key stakeholders can participate with democratic voting on meeting times.
                    </p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Vote className="w-8 h-8 text-purple-500" />
                    </div>
                    <h3 className="font-semibold mb-2">Decision Making</h3>
                    <p className="text-sm text-muted-foreground">
                      Use voting systems to make fair decisions that accommodate the majority preference.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Section 3: Group Event Coordination */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold mb-8 text-center">
              Advanced <strong>Group Event Coordination</strong> Strategies
            </h2>
            <div className="space-y-8">
              <Card className="border-0 shadow-glass bg-gradient-card backdrop-blur-glass">
                <CardHeader>
                  <h3 className="text-2xl font-semibold">Planning Large Group Events</h3>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    When coordinating events for large groups, traditional email chains become inefficient. 
                    Modern <strong>group event coordination tools</strong> solve this with centralized voting and communication.
                  </p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold mb-3">Best Practices:</h4>
                      <ul className="space-y-2 text-sm">
                        <li>• Set clear voting deadlines</li>
                        <li>• Provide multiple date and time options</li>
                        <li>• Include location voting for venue selection</li>
                        <li>• Send reminder notifications before deadline</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-3">Common Challenges:</h4>
                      <ul className="space-y-2 text-sm">
                        <li>• Different time zones and schedules</li>
                        <li>• Last-minute availability changes</li>
                        <li>• Low response rates to invitations</li>
                        <li>• Conflicting preferences and priorities</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Section 4: Online Event Voting Systems */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold mb-8 text-center">
              Mastering <strong>Online Event Voting Systems</strong>
            </h2>
            <Card className="border-0 shadow-glass bg-gradient-card backdrop-blur-glass">
              <CardContent className="p-8">
                <p className="text-lg text-muted-foreground mb-6">
                  <strong>Online event voting systems</strong> are the backbone of modern collaborative scheduling. 
                  Learn how to set up effective voting that leads to optimal outcomes for all participants.
                </p>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Voting Types</h3>
                    <div className="space-y-4">
                      <div className="border-l-4 border-primary pl-4">
                        <h4 className="font-medium">Date Voting</h4>
                        <p className="text-sm text-muted-foreground">Allow participants to vote on their preferred event dates</p>
                      </div>
                      <div className="border-l-4 border-accent pl-4">
                        <h4 className="font-medium">Time Voting</h4>
                        <p className="text-sm text-muted-foreground">Find the optimal time slot that works for most attendees</p>
                      </div>
                      <div className="border-l-4 border-success pl-4">
                        <h4 className="font-medium">Location Voting</h4>
                        <p className="text-sm text-muted-foreground">Choose venues based on accessibility and preference</p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Success Metrics</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span>Response Rate</span>
                        <span className="font-semibold">85%+</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Time Saved</span>
                        <span className="font-semibold">75%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Satisfaction</span>
                        <span className="font-semibold">9.2/10</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Call to Action */}
          <div className="text-center bg-gradient-card backdrop-blur-glass rounded-3xl p-12 shadow-elevated">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Transform Your Event Planning?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands who've discovered the best <strong>tools to find meeting times</strong> and <strong>compare scheduling apps</strong> with our platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                variant="hero" 
                size="xl" 
                onClick={() => navigate('/auth')}
                className="text-lg px-12 py-6 rounded-2xl shadow-elevated hover:shadow-glow transition-all"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Start Your First Event
              </Button>
              <Button 
                variant="glass" 
                size="xl" 
                onClick={() => navigate('/contact')}
                className="text-lg px-12 py-6 rounded-2xl"
              >
                Learn More
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventPlanningGuide;